// Storage quota reporting and durability request.
// Wraps the StorageManager API so the rest of the code never touches
// navigator.storage directly.

export interface QuotaInfo {
  usedBytes: number
  quotaBytes: number
  usedMB: number
  quotaMB: number
  percent: number
}

export async function getQuota(): Promise<QuotaInfo | null> {
  if (typeof navigator === 'undefined') return null
  if (!navigator.storage?.estimate) return null

  try {
    const estimate = await navigator.storage.estimate()
    const usedBytes = estimate.usage ?? 0
    const quotaBytes = estimate.quota ?? 0
    const usedMB = Math.round(usedBytes / (1024 * 1024))
    const quotaMB = Math.round(quotaBytes / (1024 * 1024))
    const percent = quotaBytes > 0 ? (usedBytes / quotaBytes) * 100 : 0

    return { usedBytes, quotaBytes, usedMB, quotaMB, percent }
  } catch (err) {
    console.warn('[offline] Quota estimate failed:', err)
    return null
  }
}

// Asks the browser to treat our storage as persistent (not auto-evicted).
// Returns true when granted or already persisted; false when denied or
// unsupported. Safe to call multiple times.
export async function requestPersistence(): Promise<boolean> {
  if (typeof navigator === 'undefined') return false
  if (!navigator.storage?.persist) return false

  try {
    const already = await navigator.storage.persisted()
    if (already) return true
    return await navigator.storage.persist()
  } catch (err) {
    console.warn('[offline] persist() failed:', err)
    return false
  }
}

export async function isPersistent(): Promise<boolean> {
  if (typeof navigator === 'undefined') return false
  if (!navigator.storage?.persisted) return false

  try {
    return await navigator.storage.persisted()
  } catch {
    return false
  }
}

// Formats a byte count into a short human-readable string.
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
